// Liens vérifiés de fichiers (contrat C0 v1.13) : photos de véhicules et cartes grises vues par le
// serveur dans les résultats des outils du MCP, servies par le même domaine (/api/fichiers/<jeton>,
// jeton signé par le serveur). Fonctions pures (sans React ni DOM), testées par node --test.
//
// Événement `file_links` {fichiers: [{url, lien, type: "image" | "pdf", libelle, vehicule?}], expire_le} :
//   - url  : adresse d'origine telle que dans le résultat de l'outil (sert à reconnaître un lien
//            écrit par le modèle), jamais chargée par l'interface ;
//   - lien : chemin du même domaine, seul chargé (image, téléchargement).
// Sécurité : une image du Markdown du modèle n'est affichée que si son adresse est exactement
// celle d'un lien vérifié de la conversation, et elle est chargée par son lien du même domaine ;
// toute autre image reste bloquée (texte alternatif seul), comme avant.

export const LIEN_FICHIER_RE = /^\/api\/fichiers\/f1\.[A-Za-z0-9_-]{16,2800}\.[A-Za-z0-9_-]{43}$/;
// Liens gardés par réponse dans l'historique (budget de `ui` : ~ 3 Ko par lien au plus)
export const FICHIERS_PAR_MESSAGE = 40;
const URL_MAX = 2048;

const str = (v, max = 160) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** Lien du même domaine sûr (« /api/fichiers/<jeton> »), sinon null. */
export const lienFichier = (value) => (typeof value === 'string' && LIEN_FICHIER_RE.test(value) ? value : null);

/** Clé de comparaison d'une adresse (forme normalisée par le navigateur), ou null. */
export function cleUrl(value) {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || raw.length > URL_MAX || !/^https?:\/\//i.test(raw)) return null;
  try {
    const u = new URL(raw);
    u.hash = '';
    return u.href;
  } catch {
    return null;
  }
}

const dateMs = (v) => {
  const t = typeof v === 'string' ? Date.parse(v) : NaN;
  return Number.isFinite(t) ? t : null;
};

/**
 * Lecture tolérante d'une liste de liens (événement ou historique) : [{url, lien, type, libelle,
 * vehicule, expireLe}] ; entrée sans adresse d'origine ou sans lien sûr écartée.
 */
export function normalizeFichiers(list, expireLe = null) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const vus = new Set();
  for (const f of list) {
    if (!f || typeof f !== 'object') continue;
    const url = typeof f.url === 'string' ? f.url.trim() : '';
    const lien = lienFichier(f.lien);
    if (!cleUrl(url) || !lien || vus.has(url)) continue;
    vus.add(url);
    out.push({
      url,
      lien,
      type: f.type === 'pdf' ? 'pdf' : 'image',
      libelle: str(f.libelle, 80) || 'Photo',
      vehicule: str(f.vehicule, 80) || null,
      expireLe: dateMs(f.expire_le ?? f.expireLe) ?? dateMs(expireLe),
    });
  }
  return out;
}

/** Liens d'un message + nouveaux liens reçus (sans doublon, plafonnés). */
export function fusionnerFichiers(actuels, nouveaux) {
  const liste = [...(Array.isArray(actuels) ? actuels : [])];
  const vus = new Set(liste.map((f) => f.url));
  for (const f of nouveaux) {
    if (vus.has(f.url) || liste.length >= FICHIERS_PAR_MESSAGE) continue;
    vus.add(f.url);
    liste.push(f);
  }
  return liste;
}

/** Forme enregistrée dans l'historique (`ui.fichiers`) : champs du contrat seulement. */
export const serializeFichiers = (list) =>
  (Array.isArray(list) ? list : []).slice(0, FICHIERS_PAR_MESSAGE).map((f) => ({
    url: f.url,
    lien: f.lien,
    type: f.type,
    libelle: f.libelle,
    ...(f.vehicule ? { vehicule: f.vehicule } : {}),
    ...(f.expireLe ? { expire_le: new Date(f.expireLe).toISOString() } : {}),
  }));

/**
 * Liens vérifiés de la conversation encore valables : Map(clé de l'adresse → lien). Le plus
 * récent l'emporte (même adresse revue dans une réponse suivante : nouvelle échéance).
 */
export function fichiersDeConversation(messages, now = Date.now()) {
  const map = new Map();
  for (const m of Array.isArray(messages) ? messages : []) {
    for (const f of m?.fichiers || []) {
      if (f.expireLe && f.expireLe <= now) continue;
      const cle = cleUrl(f.url);
      if (cle) map.set(cle, f);
    }
  }
  return map;
}

/** Signature stable d'une liste de liens (mémorisation du contexte de la conversation). */
export const signatureFichiers = (map) => [...map.values()].map((f) => `${f.url}|${f.lien}`).join('\n');

/** Lien vérifié d'une adresse écrite par le modèle (lien ou image Markdown), ou null. */
export function lienVerifie(map, href) {
  if (!map || !map.size) return null;
  const cle = cleUrl(href);
  return cle ? map.get(cle) ?? null : null;
}

/** Adresse de téléchargement (pièce jointe, nom proposé) d'un lien du même domaine. */
export const lienTelechargement = (lien, nom = '') =>
  lienFichier(lien) ? `${lien}?telecharger=1${nom ? `&nom=${encodeURIComponent(nom)}` : ''}` : null;
